import { useEffect, useLayoutEffect, useRef } from 'react';

/** Lo que se espera, por omisión, a que la persona deje de escribir. Menos, y cada tecla es una consulta. */
export const ESPERA_BUSQUEDA_MS = 300;

/**
 * Aplica lo escrito cuando la persona deja de escribir (#79): `aplicar(valor)`
 * corre `esperaMs` después de la última tecla, y solo si `pendiente`. Antes era
 * el mismo `useEffect` copiado en cada pantalla con buscador.
 *
 *   useBusquedaDiferida(texto, (buscar) => filtrar({ buscar }), { pendiente: texto !== url.buscar });
 *
 * - **`pendiente`** dice si lo escrito todavía no se aplicó. Va como booleano y
 *   no como «lo aplicado» porque eso suele ser un objeto nuevo en cada dibujado
 *   —los filtros de la URL—, y con él en las dependencias la espera volvería a
 *   empezar cada vez.
 * - **`aplicar`** puede cambiar en cada dibujado: se usa la última, sin
 *   reiniciar la espera.
 * - **`esperaMs`** queda en manos de la pantalla, que sabe cuánto le cuesta su
 *   consulta.
 */
export function useBusquedaDiferida<T>(
  valor: T,
  aplicar: (valor: T) => void,
  { pendiente = true, esperaMs = ESPERA_BUSQUEDA_MS }: { pendiente?: boolean; esperaMs?: number } = {},
) {
  const ultima = useRef(aplicar);
  // Antes que el efecto de abajo: cuando la espera vence, ya está la última.
  useLayoutEffect(() => {
    ultima.current = aplicar;
  });

  useEffect(() => {
    if (!pendiente) return;
    const espera = setTimeout(() => ultima.current(valor), esperaMs);
    return () => clearTimeout(espera);
  }, [valor, pendiente, esperaMs]);
}
